/**
 * Humantra Bundle Builder
 * Progress bar, flexible quantities, tier-based pricing, and cart functionality
 */

(function() {
  'use strict';

  if (!window.humantraBundleConfig) {
    console.error('Humantra Bundle Builder: Configuration not found');
    return;
  }

  const config = window.humantraBundleConfig;

  // State Management - NO tier selection, flexible quantities
  const state = {
    flavorQuantities: {},
    isSubscription: true,
    subscriptionFrequency: '',
    calculated: {
      totalQuantity: 0,
      progressPercent: 0,
      currentMilestone: 0,
      baseTotal: 0,
      compareTotal: 0,
      discountedTotal: 0,
      onetimeTotal: 0,
      savingsAmount: 0,
      savingsPercentage: 0,
      perUnitPrices: {},
      freeGiftUnlocked: false
    },
    validation: {
      isValid: false,
      errors: []
    }
  };

  // Initialize flavor quantities to 0
  config.products.forEach(product => {
    state.flavorQuantities[product.variantId] = 0;
  });

  // Derive milestones from tiers (only tiers with a milestone label)
  config.milestones = (config.tiers || [])
    .filter(t => t.milestoneLabel && t.milestoneLabel.trim())
    .map(t => t.qty);

  // DOM Elements Cache
  let elements = {};

  /**
   * Initialize the bundle builder
   */
  function init() {
    cacheElements();
    bindEvents();
    updatePriceCalculations();
    updateUI();
    initGallery();
  }

  /**
   * Cache DOM elements
   */
  function cacheElements() {
    const section = document.querySelector(`[data-section-id="${config.sectionId}"]`);
    if (!section) return;

    elements = {
      section,
      // Price displays
      currentPrice: section.querySelector('[data-current-price]'),
      comparePrice: section.querySelector('[data-compare-price]'),
      saveBadge: section.querySelector('[data-save-badge]'),
      savePercent: section.querySelector('[data-save-percent]'),
      // Progress bar
      progressFill: section.querySelector('[data-progress-fill]'),
      progressMarkers: section.querySelectorAll('.progress__marker'),
      tierPrices: section.querySelectorAll('[data-tier-price]'),
      savingsPercent: section.querySelector('[data-savings-percent]'),
      // Flavor cards
      flavorCards: section.querySelectorAll('.flavor-card'),
      // Gift
      giftContainer: section.querySelector('.bundle-builder__gift'),
      giftLockedText: section.querySelector('.gift__locked-text'),
      giftUnlockedText: section.querySelector('.gift__unlocked-text'),
      // Purchase options
      purchaseOptions: section.querySelectorAll('.purchase-option'),
      subscriptionPrice: section.querySelector('[data-subscription-price]'),
      subscriptionCompare: section.querySelector('[data-subscription-compare]'),
      onetimePrice: section.querySelector('[data-onetime-price]'),
      frequencyLabel: section.querySelector('[data-frequency-label]'),
      // Add to cart
      addToCartButton: section.querySelector('.bundle-builder__add-to-cart'),
      atcPrice: section.querySelector('[data-atc-price]'),
      atcCompare: section.querySelector('[data-atc-compare]'),
      // Buy Once
      buyOnceButton: section.querySelector('.bundle-builder__buy-once'),
      buyOncePrice: section.querySelector('[data-buy-once-price]'),
      buyOnceShipping: section.querySelector('[data-buy-once-shipping]'),
      // Error
      errorContainer: section.querySelector('.bundle-builder__error'),
      errorMessage: section.querySelector('[data-error-message]'),
      // Gallery
      galleryFeatured: section.querySelector('.gallery__featured-image'),
      thumbnails: section.querySelectorAll('.gallery__thumbnail'),
      carousel: section.querySelector('.gallery__carousel'),
      carouselSlides: section.querySelectorAll('.gallery__slide'),
      carouselDots: section.querySelectorAll('.gallery__dot'),
      // Accordions
      accordionToggles: section.querySelectorAll('[data-accordion-toggle]')
    };
  }

  /**
   * Bind all event listeners
   */
  function bindEvents() {
    // Flavor card interactions
    elements.flavorCards.forEach(card => {
      const addBtn = card.querySelector('[data-action="add"]');
      const minusBtn = card.querySelector('[data-action="decrease"]');
      const plusBtn = card.querySelector('[data-action="increase"]');
      const variantId = card.dataset.variantId;

      if (addBtn) {
        addBtn.addEventListener('click', () => handleAddFlavor(variantId, card));
      }
      if (minusBtn) {
        minusBtn.addEventListener('click', () => handleQuantityChange(variantId, -1, card));
      }
      if (plusBtn) {
        plusBtn.addEventListener('click', () => handleQuantityChange(variantId, 1, card));
      }
    });

    // Purchase type toggle
    elements.purchaseOptions.forEach(option => {
      option.addEventListener('click', () => handlePurchaseTypeChange(option));
    });

    // Add to cart
    if (elements.addToCartButton) {
      elements.addToCartButton.addEventListener('click', handleAddToCart);
    }

    // Buy Once
    if (elements.buyOnceButton) {
      elements.buyOnceButton.addEventListener('click', handleBuyOnce);
    }

    // Gallery thumbnails
    elements.thumbnails.forEach(thumb => {
      thumb.addEventListener('click', () => handleThumbnailClick(thumb));
    });

    // Gallery dots (mobile)
    elements.carouselDots.forEach(dot => {
      dot.addEventListener('click', () => handleDotClick(dot));
    });

    // Mobile carousel scroll
    if (elements.carousel) {
      elements.carousel.addEventListener('scroll', handleCarouselScroll);
    }

    // Accordion toggles
    elements.accordionToggles.forEach(toggle => {
      toggle.addEventListener('click', handleAccordionToggle);
    });
  }

  /**
   * Apply _offer property to a properties object if enabled in config
   */
  function applyOfferProperty(properties) {
    if (config.offerPropertyEnabled && config.offerPropertyValue) {
      properties['_offer'] = config.offerPropertyValue;
    }
    return properties;
  }

  /**
   * Get active tier for a given quantity (last tier where qty >= tier.qty)
   */
  function getActiveTier(quantity) {
    if (!config.tiers || !config.tiers.length) return null;
    let matched = null;
    for (const tier of config.tiers) {
      if (quantity >= tier.qty) matched = tier;
    }
    return matched;
  }

  /**
   * Get OTP discount fraction for a given quantity
   */
  function getOtpDiscount(quantity) {
    const tier = getActiveTier(quantity);
    return tier ? tier.otpPct / 100 : 0;
  }

  /**
   * Get subscription effective discount fraction for a given quantity
   */
  function getSubDiscount(quantity) {
    const tier = getActiveTier(quantity);
    return tier ? tier.subPct / 100 : 0;
  }

  /**
   * Handle clicking "ADD +" button
   */
  function handleAddFlavor(variantId, card) {
    const product = config.products.find(p => p.variantId === parseInt(variantId));
    if (!product || !product.inStock) return;

    state.flavorQuantities[variantId] = 1;

    // Switch from ADD+ button to quantity controls
    const addBtn = card.querySelector('.flavor-card__add-btn');
    const qtyControls = card.querySelector('.flavor-card__quantity');
    if (addBtn) addBtn.style.display = 'none';
    if (qtyControls) qtyControls.style.display = 'flex';

    trackEvent('bundle_flavor_added', {
      product_id: product.id,
      product_title: product.title,
      quantity: 1,
      current_bundle_total: state.calculated.totalQuantity + 1
    });

    updatePriceCalculations();
    updateUI();
  }

  /**
   * Handle quantity change (+/-)
   */
  function handleQuantityChange(variantId, delta, card) {
    const currentQty = state.flavorQuantities[variantId] || 0;
    const newQty = currentQty + delta;

    if (newQty < 0) return;

    const product = config.products.find(p => p.variantId === parseInt(variantId));
    if (!product) return;

    // Check inventory
    if (delta > 0 && product.inventoryPolicy === 'deny') {
      if (newQty > product.inventoryQuantity) {
        showError(`Only ${product.inventoryQuantity} available for ${product.title}`);
        return;
      }
    }

    state.flavorQuantities[variantId] = newQty;

    // If quantity becomes 0, switch back to ADD+ button
    if (newQty === 0) {
      const addBtn = card.querySelector('.flavor-card__add-btn');
      const qtyControls = card.querySelector('.flavor-card__quantity');
      if (addBtn) addBtn.style.display = 'block';
      if (qtyControls) qtyControls.style.display = 'none';
    }

    updatePriceCalculations();
    updateUI();
  }

  /**
   * Handle purchase type change
   */
  function handlePurchaseTypeChange(option) {
    const purchaseType = option.dataset.purchaseType;
    const isSubscription = purchaseType === 'subscription';

    if (state.isSubscription === isSubscription) return;

    state.isSubscription = isSubscription;

    elements.purchaseOptions.forEach(opt => {
      const isActive = opt.dataset.purchaseType === purchaseType;
      opt.classList.toggle('purchase-option--active', isActive);
    });

    trackEvent('bundle_purchase_type_changed', { type: purchaseType });

    updatePriceCalculations();
    updateUI();
  }

  /**
   * Calculate all prices
   */
  function updatePriceCalculations() {
    let totalQuantity = 0;
    let baseTotal = 0;
    let compareTotal = 0;

    // Calculate totals from selected flavors
    Object.entries(state.flavorQuantities).forEach(([variantId, qty]) => {
      if (qty > 0) {
        totalQuantity += qty;
        const product = config.products.find(p => p.variantId === parseInt(variantId));
        if (product) {
          baseTotal += product.price * qty;
          compareTotal += (product.compareAtPrice || product.price) * qty;
        }
      }
    });

    state.calculated.totalQuantity = totalQuantity;

    // Calculate progress percentage (max at milestone 3)
    state.calculated.progressPercent = calculateProgressPercent(totalQuantity, config.milestones);

    // Determine current milestone reached
    state.calculated.currentMilestone = 0;
    config.milestones.forEach((milestone, index) => {
      if (totalQuantity >= milestone) {
        state.calculated.currentMilestone = index + 1;
      }
    });

    // Get discounts based on quantity and purchase type
    const subDiscount = getSubDiscount(totalQuantity);
    const otpDiscount = getOtpDiscount(totalQuantity);

    // Calculate subscription price (effective sub discount from base)
    let subscriptionTotal = baseTotal * (1 - subDiscount);

    // Calculate one-time price (OTP bulk discount)
    let onetimeTotal = baseTotal * (1 - otpDiscount);

    // Calculate per-unit prices for each milestone
    const avgProductPrice = config.products.length > 0
      ? config.products.reduce((sum, p) => sum + p.price, 0) / config.products.length
      : 0;

    config.milestones.forEach((milestone) => {
      const discount = state.isSubscription ? getSubDiscount(milestone) : getOtpDiscount(milestone);
      state.calculated.perUnitPrices[milestone] = avgProductPrice * (1 - discount);
    });

    state.calculated.baseTotal = baseTotal;
    state.calculated.compareTotal = compareTotal;
    state.calculated.discountedTotal = state.isSubscription ? subscriptionTotal : onetimeTotal;
    state.calculated.onetimeTotal = onetimeTotal;
    state.calculated.savingsAmount = compareTotal - state.calculated.discountedTotal;
    state.calculated.savingsPercentage = compareTotal > 0
      ? Math.round((state.calculated.savingsAmount / compareTotal) * 100)
      : 0;

    // Check free gift unlock
    const giftThreshold = config.freeGiftThreshold || 2;
    const giftSubscriptionOnly = config.freeGiftSubscriptionOnly;
    state.calculated.freeGiftUnlocked =
      totalQuantity >= giftThreshold &&
      (!giftSubscriptionOnly || state.isSubscription);

    // Update delivery frequency label and selling plan based on current quantity
    const activeTier = getActiveTier(totalQuantity);
    if (elements.frequencyLabel) {
      elements.frequencyLabel.textContent = activeTier ? activeTier.deliveryLabel : '';
    }
    state.subscriptionFrequency = activeTier ? activeTier.sellingPlanId : '';

    // Validate - just needs at least 1 item
    validateSelection();
  }

  /**
   * Calculate progress percentage based on milestones
   * First milestone = 0%, last milestone = 100%
   */
  function calculateProgressPercent(quantity, milestones) {
    const numMilestones = milestones.length;
    
    // At or below first milestone = 0%
    if (quantity <= milestones[0]) {
      return 0;
    }
    
    // At or above last milestone = 100%
    if (quantity >= milestones[numMilestones - 1]) {
      return 100;
    }
    
    // Percentage step between milestones
    const stepPercent = 100 / (numMilestones - 1);
    
    // Find which segment we're in
    for (let i = 0; i < numMilestones - 1; i++) {
      const currentMilestone = milestones[i];
      const nextMilestone = milestones[i + 1];
      
      if (quantity > currentMilestone && quantity <= nextMilestone) {
        const segmentStart = i * stepPercent;
        const progressInSegment = (quantity - currentMilestone) / (nextMilestone - currentMilestone);
        return segmentStart + (progressInSegment * stepPercent);
      }
    }
    
    return 0;
  }


  /**
   * Validate selection
   */
  function validateSelection() {
    const errors = [];

    if (state.calculated.totalQuantity < 1) {
      errors.push('Add at least one item to your bundle');
    }

    // Check inventory
    Object.entries(state.flavorQuantities).forEach(([variantId, qty]) => {
      if (qty > 0) {
        const product = config.products.find(p => p.variantId === parseInt(variantId));
        if (product && product.inventoryPolicy === 'deny' && qty > product.inventoryQuantity) {
          errors.push(`Only ${product.inventoryQuantity} available for ${product.title}`);
        }
      }
    });

    state.validation.errors = errors;
    state.validation.isValid = errors.length === 0 && state.calculated.totalQuantity >= 1;
  }

  /**
   * Update all UI elements
   */
  function updateUI() {
    // Compute display values — show tier-1 defaults when bundle is empty
    let displayPrice = state.calculated.discountedTotal;
    let displayCompare = state.calculated.compareTotal;
    let displaySavingsAmt = state.calculated.savingsAmount;
    let displaySavingsPct = state.calculated.savingsPercentage;
    let displaySubPrice = state.calculated.baseTotal * (1 - getSubDiscount(state.calculated.totalQuantity));
    let displayOnetimePrice = state.calculated.onetimeTotal;

    if (state.calculated.totalQuantity === 0 && config.products.length > 0) {
      const avgPrice = config.products.reduce((sum, p) => sum + p.price, 0) / config.products.length;
      const avgCompare = config.products.reduce((sum, p) => sum + (p.compareAtPrice || p.price), 0) / config.products.length;
      const firstTier = config.tiers && config.tiers.length > 0 ? config.tiers[0] : null;
      const subPct = firstTier ? firstTier.subPct : 0;
      const otpPct = firstTier ? firstTier.otpPct : 0;
      const defaultPct = state.isSubscription ? subPct : otpPct;
      displayPrice = avgPrice * (1 - defaultPct / 100);
      displayCompare = avgCompare;
      displaySavingsAmt = avgCompare - displayPrice;
      displaySavingsPct = Math.floor(defaultPct);
      displaySubPrice = avgPrice * (1 - subPct / 100);
      displayOnetimePrice = avgPrice * (1 - otpPct / 100);
    }

    // Update main price display
    if (elements.currentPrice) {
      elements.currentPrice.textContent = formatMoney(displayPrice);
    }
    if (elements.comparePrice) {
      if (displaySavingsAmt > 0) {
        elements.comparePrice.textContent = formatMoney(displayCompare);
        elements.comparePrice.style.display = 'inline';
      } else {
        elements.comparePrice.style.display = 'none';
      }
    }

    // Update progress bar
    if (elements.progressFill) {
      elements.progressFill.style.width = state.calculated.progressPercent + '%';
    }

    // Update progress markers
    elements.progressMarkers.forEach((marker, index) => {
      const reached = state.calculated.currentMilestone > index;
      marker.classList.toggle('reached', reached);
    });

    // Update tier prices
    elements.tierPrices.forEach(el => {
      const milestone = parseInt(el.dataset.tierPrice);
      const price = state.calculated.perUnitPrices[milestone] || 0;
      el.textContent = formatMoney(price) + '/ea';
    });

    // Update savings badge
    if (elements.savingsPercent) {
      elements.savingsPercent.textContent = displaySavingsPct + '%';
    }

    // Update flavor card quantities
    elements.flavorCards.forEach(card => {
      const variantId = card.dataset.variantId;
      const qty = state.flavorQuantities[variantId] || 0;
      const qtyDisplay = card.querySelector('[data-quantity]');
      if (qtyDisplay) {
        qtyDisplay.textContent = qty;
      }
    });

    // Update free gift
    updateFreeGiftDisplay();

    // Update purchase option prices
    if (elements.subscriptionPrice) {
      elements.subscriptionPrice.textContent = formatMoney(displaySubPrice);
    }
    if (elements.subscriptionCompare) {
      if (displaySavingsAmt > 0) {
        elements.subscriptionCompare.textContent = formatMoney(displayCompare);
        elements.subscriptionCompare.style.display = 'inline';
      } else {
        elements.subscriptionCompare.style.display = 'none';
      }
    }
    if (elements.onetimePrice) {
      elements.onetimePrice.textContent = formatMoney(displayOnetimePrice);
    }

    // Update Add to Cart button
    if (elements.atcPrice) {
      elements.atcPrice.textContent = formatMoney(displayPrice);
    }
    if (elements.atcCompare) {
      if (displaySavingsAmt > 0) {
        elements.atcCompare.textContent = formatMoney(displayCompare);
        elements.atcCompare.style.display = 'inline';
      } else {
        elements.atcCompare.style.display = 'none';
      }
    }
    if (elements.addToCartButton) {
      elements.addToCartButton.disabled = !state.validation.isValid;
    }

    // Update Buy Once button
    if (elements.buyOncePrice) {
      elements.buyOncePrice.textContent = formatMoney(displayOnetimePrice);
    }
    if (elements.buyOnceShipping) {
      const shippingCost = config.buyOnceShippingCost || 695;
      elements.buyOnceShipping.textContent = formatMoney(shippingCost);
    }
    if (elements.buyOnceButton) {
      elements.buyOnceButton.disabled = !state.validation.isValid;
    }

    // Hide errors when valid
    if (elements.errorContainer) {
      elements.errorContainer.style.display = 'none';
    }
  }

  /**
   * Update free gift display
   */
  function updateFreeGiftDisplay() {
    if (!elements.giftContainer) return;

    const isUnlocked = state.calculated.freeGiftUnlocked;
    elements.giftContainer.classList.toggle('gift--unlocked', isUnlocked);

    if (elements.giftLockedText) {
      elements.giftLockedText.style.display = isUnlocked ? 'none' : 'block';
    }
    if (elements.giftUnlockedText) {
      elements.giftUnlockedText.style.display = isUnlocked ? 'block' : 'none';
    }
  }

  /**
   * Handle buy once (one-time purchase)
   */
  async function handleBuyOnce() {
    if (!state.validation.isValid) return;

    const button = elements.buyOnceButton;
    const originalHTML = button.innerHTML;

    elements.section.classList.add('is-loading');
    button.disabled = true;
    button.innerHTML = '<span>Adding...</span>';

    try {
      const bundleId = generateBundleId();
      const cartItems = [];

      Object.entries(state.flavorQuantities).forEach(([variantId, qty]) => {
        if (qty > 0) {
          const item = {
            id: parseInt(variantId),
            quantity: qty,
            properties: applyOfferProperty({
              '_bundle_id': bundleId,
              '_bundle_quantity': state.calculated.totalQuantity,
              '_purchase_type': 'one-time'
            })
          };

          // No selling_plan for one-time purchases
          cartItems.push(item);
        }
      });

      // Note: Free gift is typically subscription-only, but you can adjust this
      // if (state.calculated.freeGiftUnlocked && config.freeGiftProducts.length > 0) {
      //   // Add gift logic if needed for one-time purchases
      // }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: cartItems })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      const data = await response.json();
      if (window.Rebuy && Rebuy.SmartCart && Rebuy.SmartCart.show) {
        Rebuy.SmartCart.show();
      }

      // Track analytics
      const selectedFlavors = [];
      Object.entries(state.flavorQuantities).forEach(([variantId, qty]) => {
        if (qty > 0) {
          const product = config.products.find(p => p.variantId === parseInt(variantId));
          if (product) {
            selectedFlavors.push({ id: product.id, title: product.title, quantity: qty });
          }
        }
      });

      trackEvent('bundle_buy_once_added_to_cart', {
        total_items: state.calculated.totalQuantity,
        is_subscription: false,
        total_value: state.calculated.onetimeTotal / 100,
        savings_percentage: state.calculated.savingsPercentage,
        flavors: selectedFlavors
      });

      document.dispatchEvent(new CustomEvent('cart:updated'));

      // Restore button state
      button.innerHTML = originalHTML;

    } catch (error) {
      console.error('Buy once error:', error);
      showError(error.message || 'Could not add to cart. Please try again.');
      button.innerHTML = originalHTML;
    } finally {
      elements.section.classList.remove('is-loading');
      button.disabled = false;
    }
  }

  /**
   * Handle add to cart
   */
  async function handleAddToCart() {
    if (!state.validation.isValid) return;

    const button = elements.addToCartButton;
    const originalHTML = button.innerHTML;

    elements.section.classList.add('is-loading');
    button.disabled = true;
    button.innerHTML = '<span>Adding...</span>';

    try {
      const bundleId = generateBundleId();
      const cartItems = [];

      Object.entries(state.flavorQuantities).forEach(([variantId, qty]) => {
        if (qty > 0) {
          const item = {
            id: parseInt(variantId),
            quantity: qty,
            properties: applyOfferProperty({
              '_bundle_id': bundleId,
              '_bundle_quantity': state.calculated.totalQuantity
            })
          };

          if (state.isSubscription) {
            const activeTier = getActiveTier(state.calculated.totalQuantity);
            if (activeTier && activeTier.sellingPlanId) {
              item.selling_plan = parseInt(activeTier.sellingPlanId);
            }
          }

          cartItems.push(item);
        }
      });

      // Add free gift if unlocked
      if (state.calculated.freeGiftUnlocked && config.freeGiftProducts.length > 0) {
        config.freeGiftProducts.forEach(gift => {
          cartItems.push({
            id: gift.variantId,
            quantity: 1,
          });
        });
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: cartItems })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      const data = await response.json();
      if (window.Rebuy && Rebuy.SmartCart && Rebuy.SmartCart.show) {
        Rebuy.SmartCart.show();
      }

      // Track analytics
      const selectedFlavors = [];
      Object.entries(state.flavorQuantities).forEach(([variantId, qty]) => {
        if (qty > 0) {
          const product = config.products.find(p => p.variantId === parseInt(variantId));
          if (product) {
            selectedFlavors.push({ id: product.id, title: product.title, quantity: qty });
          }
        }
      });

      trackEvent('bundle_added_to_cart', {
        total_items: state.calculated.totalQuantity,
        is_subscription: state.isSubscription,
        total_value: state.calculated.discountedTotal / 100,
        savings_percentage: state.calculated.savingsPercentage,
        free_gift_included: state.calculated.freeGiftUnlocked,
        flavors: selectedFlavors
      });

      document.dispatchEvent(new CustomEvent('cart:updated'));

      // Restore button state
      button.innerHTML = originalHTML;

    } catch (error) {
      console.error('Add to cart error:', error);
      showError(error.message || 'Could not add to cart. Please try again.');
      button.innerHTML = originalHTML;
    } finally {
      elements.section.classList.remove('is-loading');
      button.disabled = false;
    }
  }

  /**
   * Show error message
   */
  function showError(message) {
    if (elements.errorMessage && elements.errorContainer) {
      elements.errorMessage.textContent = message;
      elements.errorContainer.style.display = 'block';
      setTimeout(() => {
        elements.errorContainer.style.display = 'none';
      }, 5000);
    }
  }

  /**
   * Generate unique bundle ID
   */
  function generateBundleId() {
    return 'bundle_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Format money
   */
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      const formatted = Shopify.formatMoney(cents, config.moneyFormat);
      // Remove 'S' before currency symbol (fix for S$ issue)
      return formatted.replace(/^S(\$|€|£|¥|₹|R\$|A\$|C\$|NZ\$)/, '$1');
    }
    const amount = (cents / 100).toFixed(2);
    let format = config.moneyFormat || '${{amount}}';
    // Remove 'S' before currency symbol in format string
    format = format.replace(/^S(\$|€|£|¥|₹|R\$|A\$|C\$|NZ\$)/, '$1');
    return format.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }

  /**
   * Track analytics event
   */
  function trackEvent(eventName, data) {
    if (typeof gtag === 'function') {
      gtag('event', eventName, data);
    }
    if (typeof analytics !== 'undefined' && analytics.track) {
      analytics.track(eventName, data);
    }
    if (typeof dataLayer !== 'undefined') {
      dataLayer.push({ event: eventName, ...data });
    }
  }

  /* ============================================
     GALLERY FUNCTIONS
     ============================================ */

  function initGallery() {
    if (elements.carousel) {
      const autoplay = elements.carousel.dataset.autoplay === 'true';
      const speed = parseInt(elements.carousel.dataset.speed) || 4000;

      if (autoplay && elements.carouselSlides.length > 1) {
        setInterval(() => {
          const currentIndex = getCurrentSlideIndex();
          const nextIndex = (currentIndex + 1) % elements.carouselSlides.length;
          scrollToSlide(nextIndex);
        }, speed);
      }
    }
  }

  function handleThumbnailClick(thumb) {
    const index = parseInt(thumb.dataset.index);

    if (elements.galleryFeatured && elements.thumbnails[index]) {
      const thumbImg = elements.thumbnails[index].querySelector('img');
      if (thumbImg) {
        const src = thumbImg.src.replace(/width=\d+/, 'width=800');
        elements.galleryFeatured.src = src;
      }
    }

    elements.thumbnails.forEach((t, i) => {
      t.classList.toggle('gallery__thumbnail--active', i === index);
    });
  }

  function handleDotClick(dot) {
    const index = parseInt(dot.dataset.index);
    scrollToSlide(index);
  }

  function scrollToSlide(index) {
    if (!elements.carousel || !elements.carouselSlides[index]) return;
    const slide = elements.carouselSlides[index];
    elements.carousel.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  function handleCarouselScroll() {
    const currentIndex = getCurrentSlideIndex();
    elements.carouselDots.forEach((dot, i) => {
      dot.classList.toggle('gallery__dot--active', i === currentIndex);
    });
  }

  function getCurrentSlideIndex() {
    if (!elements.carousel || elements.carouselSlides.length === 0) return 0;
    const scrollLeft = elements.carousel.scrollLeft;
    const slideWidth = elements.carouselSlides[0].offsetWidth;
    return Math.round(scrollLeft / slideWidth);
  }

  /**
   * Handle accordion toggle
   */
  function handleAccordionToggle(e) {
    const button = e.currentTarget;
    const accordion = button.closest('.accordion');
    const body = accordion.querySelector('[data-accordion-body]');
    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    const plusIcon = button.querySelector('.accordion__icon--plus');
    const minusIcon = button.querySelector('.accordion__icon--minus');

    // Toggle state
    const newState = !isExpanded;
    button.setAttribute('aria-expanded', newState);
    accordion.classList.toggle('accordion--expanded', newState);

    // Toggle icons
    if (plusIcon) plusIcon.style.display = newState ? 'none' : 'block';
    if (minusIcon) minusIcon.style.display = newState ? 'block' : 'none';

    // Animate body
    if (body) {
      if (newState) {
        body.style.maxHeight = body.scrollHeight + 'px';
      } else {
        body.style.maxHeight = '0px';
      }
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external access
  window.HumantraBundleBuilder = {
    getState: () => ({ ...state }),
    updateUI,
    refresh: () => { updatePriceCalculations(); updateUI(); }
  };

})();
